/* eslint-disable max-lines */
import "./same-day-badge.scss";
import { generateTimeRemainingText } from "common/utils/ui/generate-time-remaining";

const SELECTORS = {
  BADGE: ".js-same-day-badge",
  TITLE: ".js-same-day-badge-title",
  TIMELINE: ".js-same-day-badge-timeline",
  TIMELINE_VALUE: ".js-same-day-badge-timeline-value"
};

const badgeInstances = new WeakMap();

class SameDayBadge {
  #badge;

  #hideTimelineElement;

  #pageType;

  #flashCutOffTime;

  #deliveryDate;

  #countdownInterval;

  get #$titleElement() {
    return this.#badge?.querySelector(SELECTORS.TITLE);
  }

  get #$timelineElement() {
    return this.#badge?.querySelector(SELECTORS.TIMELINE);
  }

  get #$timelineValueElement() {
    return this.#badge?.querySelector(SELECTORS.TIMELINE_VALUE);
  }

  /**
   * Creates a new SameDayBadge instance
   * @param {HTMLElement} $badge - The badge element
   * @param {Object} options - Configuration options
   * @param {Date} options.flashCutOffTime - Cut off time eg: 2025-05-12T14:00:01.357-04:00
   * @param {Date} options.deliveryDate - Delivery date, e.g. "Today, June 18", "Tomorrow, June 19", or "Friday, June 20"
   * @param {string} options.pageType - The type of page (e.g. "plp" or "pdp")
   */
  constructor($badge, options = {}) {
    this.#badge = $badge;
    this.#hideTimelineElement = this.#badge?.dataset.hideTimeline === "true";

    this.#flashCutOffTime = options.flashCutOffTime;
    this.#deliveryDate = options.deliveryDate;
    this.#pageType = options.pageType;

    this.#initialize();
  }

  /**
   * Checks if the current day is a weekend
   * @returns {boolean} True if the current day is a weekend, false otherwise
   */
  static #isWeekend() {
    const now = new Date();
    const dayOfWeek = now.getDay();

    return dayOfWeek === 0 || dayOfWeek === 6;
  }

  /**
   * Checks if delivery date is today
   * @returns {boolean}
   */
  #isSameDay() {
    if (!this.#deliveryDate) return false;

    return this.#deliveryDate.toLowerCase().includes("today");
  }

  /**
   * Checks if delivery date is tomorrow
   * @returns {boolean}
   */
  #isNextDay() {
    if (!this.#deliveryDate) return false;

    return this.#deliveryDate.toLowerCase().includes("tomorrow");
  }

  /**
   * Initialize the badge with configuration values
   * @returns {SameDayBadge} The current instance for chaining
   */
  #initialize() {
    if (!this.#hideTimelineElement && !SameDayBadge.#isWeekend() && this.#flashCutOffTime) {
      this.#startCountdown();
    }

    this.#updateBadgeText();

    this.toggleBadge(true);

    return this;
  }

  /**
   * Sets the badge text
   * @param {string} text - The text to display in the badge
   * @returns {SameDayBadge} The current instance for chaining
   */
  #setBadgeText(text) {
    if (!this.#badge || !text) {
      return this;
    }

    this.#$titleElement.textContent = text;

    return this;
  }

  /**
   * Sets the badge timeline
   * @param {string} timeline - The timeline to display in the badge (e.g. "2h 21m 5s")
   * @returns {SameDayBadge} The current instance for chaining
   */
  #setBadgeTimeline(timeline = "0h 0m 0s") {
    if (!this.#badge || !timeline || this.#hideTimelineElement) {
      return this;
    }

    if (this.#$timelineValueElement) {
      this.#$timelineValueElement.textContent = timeline;
    }

    return this;
  }

  /**
   * Checks if the current time is past the flash cut off time
   * @returns {boolean} True if current time is past the cut off time, false otherwise
   */
  #isFlashCutOffTimeInPast() {
    if (!this.#isSameDay()) return true;
    if (!this.#flashCutOffTime) return true;

    const now = new Date();
    const cutoffDate = new Date(this.#flashCutOffTime);
    return now > cutoffDate;
  }

  /**
   * Hides the timeline element
   */
  #hideTimeline() {
    this.#$timelineElement.classList.add("hidden");
  }

  /**
   * Starts the countdown timer that updates the badge timeline
   */
  #startCountdown() {
    if (this.#countdownInterval) {
      clearInterval(this.#countdownInterval);
    }

    this.#updateCountdown();

    this.#countdownInterval = setInterval(() => {
      this.#updateCountdown();
    }, 60000);
  }

  /**
   * Updates the countdown display with time remaining until flashCutOffTime
   */
  #updateCountdown() {
    if (!this.#flashCutOffTime || this.#isFlashCutOffTimeInPast()) {
      this.#hideTimeline();
      this.#updateBadgeText();
      return;
    }

    const cutoffTime = new Date(this.#flashCutOffTime).getTime();
    const timeRemaining = generateTimeRemainingText(cutoffTime, true);

    if (!timeRemaining) {
      clearInterval(this.#countdownInterval);
      this.#setBadgeTimeline();
      this.#hideTimeline();
      this.#updateBadgeText();
      return;
    }

    this.#setBadgeTimeline(timeRemaining);
  }

  /**
   * Updates the badge text based on current time and weekend status
   * @returns {SameDayBadge} The current instance for chaining
   */
  #updateBadgeText() {
    if (SameDayBadge.#isWeekend()) {
      this.toggleBadge(false);
      return this;
    }

    let message;

    if (this.#isSameDay()) {
      message =
        this.#hideTimelineElement && this.#pageType === "pdp"
          ? "Get it today by 9PM!"
          : "Get it today!";
    } else if (this.#isNextDay()) {
      message = "Get it tomorrow!";
    } else {
      return this;
    }

    this.#setBadgeText(message);
    return this;
  }

  /**
   * Toggles the visibility of the badge
   * @param {boolean} show - Whether to show or hide the badge
   * @returns {void}
   */
  toggleBadge(show = true) {
    this.#badge.classList.toggle("hidden", !show);
  }

  /**
   * Updates the badge options with new values
   * @param {Object} options - Configuration options
   * @param {Date} [options.flashCutOffTime] - New cut off time
   * @param {Date} [options.deliveryDate] - New delivery date
   * @param {string} [options.pageType] - New page type
   * @returns {SameDayBadge} The current instance for chaining
   */
  updateOptions(options = {}) {
    if (options.flashCutOffTime) {
      this.#flashCutOffTime = options.flashCutOffTime;

      if (!this.#hideTimelineElement && !SameDayBadge.#isWeekend()) {
        this.#startCountdown();
      }
    }

    if (options.deliveryDate) {
      this.#deliveryDate = options.deliveryDate;
    }

    if (options.pageType) {
      this.#pageType = options.pageType;
    }

    this.#updateBadgeText();
    return this;
  }
}

/**
 * Creates a new SameDayBadge instance if the parent element contains a badge
 * @param {HTMLElement} $parent - The parent element
 * @param {Object} options - Configuration options
 * @param {Date} options.flashCutOffTime - Cut off time
 * @param {string} options.pageType - The type of page (e.g. "plp" or "pdp")
 * @returns {SameDayBadge|{initialize: Function}} The SameDayBadge instance or a default object with empty initialize method
 */
export default function createSameDayBadge($parent, options = {}) {
  if (!$parent || !($parent instanceof Element)) {
    return null;
  }

  const $badge = $parent.querySelector(SELECTORS.BADGE);

  if (!$badge) {
    return null;
  }

  const existingBadge = badgeInstances.get($parent);
  if (existingBadge) return existingBadge.updateOptions(options);

  const newBadge = new SameDayBadge($badge, options);
  badgeInstances.set($parent, newBadge);
  return newBadge;
}
